import React, { useState, useEffect, useRef } from "react";
import { products } from "../../constants";
import { FiX } from "react-icons/fi";
import { FaShoppingCart, FaWhatsapp } from "react-icons/fa";
import { QRCodeSVG } from 'qrcode.react';

const Products = () => {
  const [selectedProduct, setSelectedProduct] = useState(null); // Used for product details modal AND cart summary modal
  const [activeCategory, setActiveCategory] = useState("Momo's");
  const [cart, setCart] = useState([]);
  const [selectedOption, setSelectedOption] = useState({});

  // NEW STATES FOR ORDER TYPE AND DETAILS
  const [showOrderTypeModal, setShowOrderTypeModal] = useState(false);
  const [orderType, setOrderType] = useState(''); // 'dine-in' or 'parcel'
  const [customerAddress, setCustomerAddress] = useState('');
  const [tableNumber, setTableNumber] = useState('');
  const [tableNumberError, setTableNumberError] = useState('');

  const categoryRefs = useRef({});

  // Effect to manage body overflow when any modal is open
  useEffect(() => {
    if (selectedProduct || showOrderTypeModal) { // Include showOrderTypeModal
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedProduct, showOrderTypeModal]); // Add showOrderTypeModal to dependencies

  useEffect(() => {
    const initialOptions = {};
    products.forEach(product => {
      if (product.options && product.options.length > 0) {
        initialOptions[product.id] = product.options[0].name;
      }
    });
    setSelectedOption(initialOptions);
  }, []);

  const handleOpenModal = (product) => {
    setSelectedProduct(product);
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
  };

  const filterProducts = (category) => {
    setActiveCategory(category);
    if (categoryRefs.current[category]) {
      categoryRefs.current[category].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const productsByCategory = products.reduce((acc, product) => {
    if (!acc[product.category]) {
      acc[product.category] = [];
    }
    acc[product.category].push(product);
    return acc;
  }, {});

  const allCategories = [
    "Momo's",
    "Noodle's",
    "Sandwich & Burger",
    "Chilli's",
    "Rice's",
    "Chaat & D'Vada",
    "Papad",
    "Roll's",
    "Chicken Menu's",
    "Mutton Menu's",
    "Egg Menu's",
    "Drinks",
  ];

  const handleOptionChange = (productId, optionName) => {
    setSelectedOption(prev => ({
      ...prev,
      [productId]: optionName
    }));
  };

  const addToCart = (product) => {
    const existingItemIndex = cart.findIndex(
      (item) => item.id === product.id && item.selectedOption === selectedOption[product.id]
    );

    if (existingItemIndex > -1) {
      setCart(prevCart =>
        prevCart.map((item, index) =>
          index === existingItemIndex
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      const chosenOption = product.options
        ? product.options.find(opt => opt.name === selectedOption[product.id])
        : null;

      setCart(prevCart => [
        ...prevCart,
        {
          id: product.id,
          title: product.title,
          category: product.category,
          image: product.image,
          selectedOption: chosenOption ? chosenOption.name : 'N/A',
          price: chosenOption ? chosenOption.price : product.price,
          quantity: 1,
        },
      ]);
    }
    console.log(`Added to cart: ${product.title} (${selectedOption[product.id] || 'N/A'})`);
  };

  const removeFromCart = (itemToRemove) => {
    setCart(prevCart =>
      prevCart.filter(item => !(item.id === itemToRemove.id && item.selectedOption === itemToRemove.selectedOption))
    );
  };

  const incrementQuantity = (itemToUpdate) => {
    setCart(prevCart =>
      prevCart.map(item =>
        (item.id === itemToUpdate.id && item.selectedOption === itemToUpdate.selectedOption)
          ? { ...item, quantity: item.quantity + 1 }
          : item
      )
    );
  };

  const decrementQuantity = (itemToUpdate) => {
    setCart(prevCart =>
      prevCart.map(item =>
        (item.id === itemToUpdate.id && item.selectedOption === itemToUpdate.selectedOption)
          ? { ...item, quantity: item.quantity - 1 }
          : item
      ).filter(item => item.quantity > 0)
    );
  };

  // NEW: Function to handle "Order Now" button click - opens order type modal
  const handleOrderNowClick = () => {
    if (cart.length === 0) {
      alert("Your cart is empty! Please add items before ordering.");
      return;
    }
    setShowOrderTypeModal(true);
    setTableNumberError(''); // Clear previous errors
  };

  // NEW: Function to proceed from order type selection to WhatsApp
  // ... (all the other code remains the same)

  const proceedToWhatsAppOrder = () => {
    let orderDetails = '';
    if (orderType === 'dine-in') {
      const tableNum = parseInt(tableNumber);
      if (isNaN(tableNum) || tableNum < 1 || tableNum > 4) {
        setTableNumberError('Table number must be between 1 and 4.');
        return;
      }
      orderDetails = `Order Type: Dine-in\nTable Number: ${tableNumber}\n\n`;
      setTableNumberError('');
    } else if (orderType === 'parcel') {
      if (!customerAddress.trim()) {
        alert('Please enter your address for parcel order.');
        return;
      }
      orderDetails = `Order Type: Parcel\nDelivery Address: ${customerAddress}\n\n`;
    } else {
      alert('Please select an order type (Dine-in or Parcel).');
      return;
    }

    let whatsappMessage = `Hi, I'd like to place an order from Food Square & Restaurant!\n\n${orderDetails}My order:\n\n`;
    let total = 0;

    cart.forEach((item, index) => {
      whatsappMessage += `${index + 1}. ${item.title}`;
      if (item.selectedOption && item.selectedOption !== 'N/A') {
        whatsappMessage += ` (${item.selectedOption})`;
      }
      whatsappMessage += ` x ${item.quantity} = ₹${item.price * item.quantity}\n`;
      total += item.price * item.quantity;
    });

    whatsappMessage += `\nTotal: ₹${total}\n\nPlease confirm my order details.`;

    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://wa.me/919795720202?text=${encodedMessage}`;
    window.open(whatsappUrl, "_blank");

    // Close the order type modal after sending to WhatsApp
    setShowOrderTypeModal(false);

    // **********************************************
    // NEW: Reset all order-related states to clear the application
    // **********************************************
    setCart([]); // This clears the cart
    setSelectedProduct(null); // This closes the cart summary modal
    setOrderType(''); // This clears the selected order type
    setTableNumber(''); // This clears the table number input
    setCustomerAddress(''); // This clears the customer address input
  };


  const totalCartItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const totalCartPrice = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  // const upiQrCodeValue = totalCartPrice > 0 ? `upi://pay?pa=8604337187@ybl&pn=Food-Square%20Restro&am=${totalCartPrice}.00&cu=INR&mc=5812` : '';


  return (
    <section
      id="products"
      className="py-24 pb-24 px-[12vw] md:px-[7vw] lg:px-[20vw] font-sans relative"
    >
      <div className="text-center mb-8">
        <h2 className="text-4xl font-bold text-white">OUR MENU</h2>
        <div className="w-32 h-1 bg-purple-500 mx-auto mt-4"></div>
        <p className="text-gray-400 mt-4 text-lg font-semibold">
          Explore our  Veg & Non-Veg delicious offerings!
        </p>
      </div>

      {/* Cart Icon (Fixed position - only visible when items are in cart) */}
      <div className="fixed bottom-8 right-8 z-[1000] flex flex-col items-end space-y-3">
        {totalCartItems > 0 && ( // This ensures the cart icon only shows if items are in cart
          <div className="bg-purple-600 text-white rounded-full p-3 shadow-lg flex items-center justify-center cursor-pointer hover:bg-purple-700 transition duration-300 relative"
            onClick={() => setSelectedProduct({ id: 'cart-summary' })}>
            <FaShoppingCart size={24} />
            <span className="absolute top-0 right-0 bg-red-500 text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center transform translate-x-1/2 -translate-y-1/2">
              {totalCartItems}
            </span>
          </div>
        )}
        {/* The separate "Order Now" button is removed from here */}
      </div>


      {/* Category Selection - THIS IS THE ELEMENT YOU WANT TO BE STICKY */}
      <div className="sticky top-0 z-50  bg-opacity-95 backdrop-blur-sm py-4 flex justify-center gap-4 mb-8 flex-wrap border-b border-gray-700 shadow-lg">
        <div className="hidden md:flex justify-center gap-4 flex-wrap">
          {allCategories.map((category) => (
            <button
              key={category}
              onClick={() => filterProducts(category)}
              className={`px-6 py-2 rounded-full text-white font-semibold transition duration-300 transform hover:scale-105 active:scale-95 ${activeCategory === category
                  ? "bg-purple-600 shadow-md"
                  : "bg-gray-800 hover:bg-gray-700"
                }`}
            >
              {category}
            </button>
          ))}
        </div>
        <div className="md:hidden w-full px-4">
          <select
            value={activeCategory}
            onChange={(e) => filterProducts(e.target.value)}
            className="w-full bg-gray-800 text-white p-3 rounded-lg text-lg font-semibold border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {allCategories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Product Display Structure */}
      <div className="mt-8">
        {allCategories.map((category) => (
          <div
            key={category}
            ref={el => categoryRefs.current[category] = el}
            className={`mb-10 ${activeCategory === category ? '' : 'hidden'}`}
            id={`category-${category}`}
          >
            <h3 className="text-3xl font-bold text-white mb-6 text-center md:text-left">
              {category.toUpperCase()}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              {productsByCategory[category] && productsByCategory[category].map((product) => (
                <div
                  key={product.id}
                  className="flex flex-col sm:flex-row items-stretch bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-4 transition-transform duration-300"
                >
                  <div className="flex-shrink-0 mb-4 sm:mb-0 sm:mr-4 flex justify-center">
                    <img
                      src={product.image}
                      alt={product.title}
                      className="w-full h-40 sm:w-28 sm:h-28 object-cover rounded-md"
                    />
                  </div>
                  <div className="flex-grow flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-start mb-1">
                        <h4 className="text-xl font-semibold text-white mr-2">
                          {product.title}
                        </h4>
                        {product.options ? (
                          <select
                            value={selectedOption[product.id] || (product.options.length > 0 ? product.options[0].name : '')}
                            onChange={(e) => handleOptionChange(product.id, e.target.value)}
                            className="bg-gray-700 text-purple-400 p-1 rounded-md text-base font-bold focus:outline-none focus:ring-1 focus:ring-purple-500 flex-shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {product.options.map(opt => (
                              <option key={opt.name} value={opt.name}>
                                {opt.name} (₹{opt.price})
                              </option>
                            ))}
                          </select>
                        ) : (
                          product.price && (
                            <p className="text-xl font-bold text-purple-400 flex-shrink-0">
                              ₹{product.price}
                            </p>
                          )
                        )}
                      </div>
                      <p className="text-gray-400 text-sm line-clamp-2">
                        {product.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {product.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); addToCart(product); }}
                      className="mt-4 bg-purple-700 text-white py-2 px-4 rounded-lg font-semibold hover:bg-purple-800 transition duration-300 flex items-center justify-center"
                    >
                      <FaShoppingCart className="mr-2" /> Add to Cart
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Cart Summary Modal */}
      {selectedProduct && selectedProduct.id === 'cart-summary' && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[1000] p-4">
          <div
            className="bg-gray-800 p-6 rounded-xl max-w-lg w-full relative transition-all duration-300 transform -translate-y-1/2 top-1/2 text-white"
            style={{ maxHeight: "90vh", overflowY: "auto" }}
          >
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors duration-200 p-2 rounded-full bg-red-500 hover:bg-red-600 shadow-lg z-[1001] flex items-center justify-center"
              aria-label="Close modal"
            >
              <FiX className="h-7 w-7 text-white" />
            </button>

            <h2 className="text-3xl font-bold mb-6 text-center">Your Cart</h2>
            {cart.length === 0 ? (
              <p className="text-center text-gray-400">Your cart is empty.</p>
            ) : (
              <>
                <ul className="space-y-4 mb-6">
                  {cart.map((item, index) => (
                    <li key={`${item.id}-${item.selectedOption}-${index}`} className="flex items-center bg-gray-700 p-3 rounded-lg shadow-md">
                      <img src={item.image} alt={item.title} className="w-16 h-16 object-cover rounded-md mr-4" />
                      <div className="flex-grow">
                        <p className="font-semibold text-lg">{item.title} {item.selectedOption && item.selectedOption !== 'N/A' && `(${item.selectedOption})`}</p>
                        <p className="text-purple-400">₹{item.price}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); decrementQuantity(item); }}
                          className="bg-red-500 hover:bg-red-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-lg font-bold"
                        >
                          -
                        </button>
                        <span className="font-bold text-xl">{item.quantity}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); incrementQuantity(item); }}
                          className="bg-green-500 hover:bg-green-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-lg font-bold"
                        >
                          +
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); removeFromCart(item); }}
                          className="ml-2 text-red-400 hover:text-red-500"
                        >
                          <FiX size={20} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="text-right text-2xl font-bold mt-4 border-t border-gray-600 pt-4">
                  Total: ₹{totalCartPrice}
                </div>

                {/* QR Code Section - Dynamically generated with total amount */}
                {/* {totalCartPrice > 0 && (
                  <div className="mt-6 p-4 bg-gray-700 rounded-lg text-center">
                    <p className="text-xl font-semibold mb-3">Scan to Pay (UPI)</p>
                    <div className="flex justify-center">
                      <QRCodeSVG
                        value={upiQrCodeValue} // This contains the dynamic amount
                        size={200}
                        level="H"
                        bgColor="#ffffff"
                        fgColor="#000000"
                      />
                    </div>
                    <p className="text-purple-300 text-sm mt-3">Scan this QR code with any UPI app to pay ₹{totalCartPrice}.00</p>
                    <p className="text-white text-xs font-semibold mt-1">UPI ID: 8604337187@ybl</p>
                  </div>
                )} */}

                <div className="flex justify-center mt-6">
                  <button
                    onClick={handleOrderNowClick}
                    className="bg-green-500 text-white px-8 py-3 rounded-lg font-semibold text-lg hover:bg-green-600 transition flex items-center justify-center shadow-lg hover:shadow-xl w-full"
                  >
                    <FaWhatsapp className="mr-2" size={20} /> Share Order on WhatsApp
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Product Details Modal (original modal logic for individual product click) */}
      {selectedProduct && selectedProduct.id !== 'cart-summary' && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[1000] p-4">
          <div
            className="bg-white p-8 rounded-xl max-w-xl w-full relative transition-all duration-300 transform -translate-y-1/2 top-1/2"
            style={{ maxHeight: "90vh", overflowY: "auto" }}
          >
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-gray-800 hover:text-black transition-colors duration-200 p-2 rounded-full bg-red-400 hover:bg-red-500 shadow-lg z-[1001] flex items-center justify-center"
              aria-label="Close modal"
            >
              <FiX className="h-7 w-7 text-white" />
            </button>

            <div className="text-center mb-4">
              {selectedProduct.id && (
                <p className="text-lg font-semibold text-gray-800">
                  Item ID: {selectedProduct.id}
                </p>
              )}
              <h2 className="text-3xl font-bold text-gray-900 mt-2">
                {selectedProduct.title}
              </h2>
              {selectedProduct.options ? (
                <p className="text-md text-purple-600 font-medium mt-1">
                  Price: ₹{selectedProduct.options.find(opt => opt.name === (selectedOption[selectedProduct.id] || (selectedProduct.options.length > 0 ? selectedProduct.options[0].name : '')))?.price || 'N/A'}
                </p>
              ) : (
                selectedProduct.price && (
                  <p className="text-md text-purple-600 font-medium mt-1">
                    Price: ₹{selectedProduct.price}
                  </p>
                )
              )}
            </div>
            <img
              src={selectedProduct.image}
              alt={selectedProduct.title}
              className="object-contain rounded-lg mb-6 shadow-md mx-auto"
              style={{ maxWidth: "100%", height: "auto" }}
            />
            <p className="text-gray-700 text-lg mb-6 leading-relaxed">
              {selectedProduct.description}
            </p>
            <div className="flex flex-wrap gap-3 mb-6 justify-center">
              {selectedProduct.tags.map((tag, index) => (
                <span
                  key={index}
                  className="bg-purple-600 text-white text-base px-4 py-1 rounded-full shadow-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
            <div className="flex justify-center mt-4">
              <button
                onClick={() => {
                  addToCart(selectedProduct);
                  handleCloseModal();
                }}
                className="bg-purple-700 text-white px-8 py-3 rounded-lg font-semibold text-lg hover:bg-purple-800 transition flex items-center justify-center shadow-lg hover:shadow-xl"
              >
                <FaShoppingCart className="mr-2" /> Add to Cart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Order Type Selection Modal (Dine-in / Parcel) */}
      {showOrderTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[1000] p-4">
          <div className="bg-gray-800 p-8 rounded-xl max-w-sm w-full relative text-white shadow-lg">
            <button
              onClick={() => setShowOrderTypeModal(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 p-2 rounded-full bg-red-500 hover:bg-red-600 shadow-lg"
              aria-label="Close"
            >
              <FiX className="h-7 w-7 text-white" />
            </button>
            <h2 className="text-3xl font-bold mb-6 text-center">How would you like your order?</h2>

            <div className="flex justify-center gap-4 mb-6">
              <button
                onClick={() => { setOrderType('dine-in'); setTableNumberError(''); }}
                className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${orderType === 'dine-in' ? 'bg-purple-600 shadow-lg' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
              >
                Dine-in
              </button>
              <button
                onClick={() => { setOrderType('parcel'); setTableNumberError(''); }}
                className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${orderType === 'parcel' ? 'bg-purple-600 shadow-lg' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
              >
                Parcel
              </button>
            </div>

            {orderType === 'dine-in' && (
              <div className="mb-6">
                <label htmlFor="tableNumber" className="block text-gray-300 text-sm font-medium mb-2">
                  Table Number (1-4):
                </label>
                <input
                  type="number"
                  id="tableNumber"
                  value={tableNumber}
                  onChange={(e) => { setTableNumber(e.target.value); setTableNumberError(''); }}
                  min="1"
                  max="4"
                  className="w-full p-3 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter table number"
                />
                {tableNumberError && (
                  <p className="text-red-400 text-sm mt-2">{tableNumberError}</p>
                )}
              </div>
            )}

            {orderType === 'parcel' && (
              <div className="mb-6">
                <label htmlFor="deliveryAddress" className="block text-gray-300 text-sm font-medium mb-2">
                  Delivery Address:
                </label>
                <textarea
                  id="deliveryAddress"
                  value={customerAddress}
                  onChange={(e) => setCustomerAddress(e.target.value)}
                  rows="3"
                  className="w-full p-3 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  placeholder="Enter your full delivery address"
                ></textarea>
              </div>
            )}

            <button
              onClick={proceedToWhatsAppOrder}
              className="w-full bg-green-500 text-white py-3 rounded-lg font-semibold text-lg hover:bg-green-600 transition shadow-lg"
              disabled={!orderType || (orderType === 'parcel' && !customerAddress.trim()) || (orderType === 'dine-in' && (!tableNumber || tableNumberError))}
            >
              Proceed to WhatsApp Order
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default Products;